import { ChangeEvent, KeyboardEvent, MouseEvent, useEffect, useRef } from "react";
import { Button, Dialog, Heading, IconButton, LockIcon, Pane, Paragraph, PlusIcon, Spinner, Text, TextInput, TrashIcon } from "evergreen-ui";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RampartAuthorized } from "../rampart/RampartAuthorized";
import { FMS_CHAT_GROUP } from "../rampart/RampartUtils";
import { EmptyState } from "../common/EmptyState";
import { useFmsChatStateManager } from "./FmsChatStateManager";
import "./styles/FmsChat.css";

const INVITE_ONLY_SUBTITLE = "The chat beta is invite-only. Ask in #eng if you'd like access.";

const InviteOnly = () => (
    <EmptyState title="Not available" subtitle={INVITE_ONLY_SUBTITLE}>
        <LockIcon size={60} />
    </EmptyState>
);

const FmsChat = () => {
    const {
        threadRows,
        activeThreadId,
        messages,
        conversationTitle,
        statusLabel,
        isBusy,
        draft,
        error,
        blocked,
        pendingDeleteTitle,
        onDraftChange,
        onSubmit,
        onOpenThread,
        onStartNew,
        onRequestDelete,
        onConfirmDelete,
        onCancelDelete
    } = useFmsChatStateManager();

    // Presentational only (VCM allows refs in the view): scroll the transcript
    // to the bottom as messages arrive or the status line changes.
    const bottomRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, statusLabel]);

    // Server-side mint rejection (403/401): the client is in the group
    // (RampartAuthorized gated this) but the server refused, so show the
    // same invite-only state instead of a transient error line.
    if (blocked) {
        return <InviteOnly />;
    }

    return (
        <Pane display="flex" height="100%">
            {/* minWidth 0 overrides the flex-item default (min-width: auto),
                which would otherwise let a wide answer stretch this column
                past the viewport and push the threads pane off-screen. */}
            <Pane flex={1} minWidth={0} display="flex" flexDirection="column" padding={24}>
                <Heading size={600} marginBottom={16}>{conversationTitle}</Heading>
                <Pane flex={1} overflowY="auto">
                    {messages.length === 0 && !isBusy && (
                        <Paragraph color="muted">
                            Ask about sales, profit, payouts, products, or launches.
                            Answers come from the live finance database.
                        </Paragraph>
                    )}
                    {messages.map((m, i) => (
                        <Pane
                            key={i}
                            marginBottom={12}
                            padding={12}
                            borderRadius={8}
                            background={m.role === "human" ? "blueTint" : "tint1"}
                            marginLeft={m.role === "human" ? 80 : 0}
                            marginRight={m.role === "human" ? 0 : 80}
                        >
                            {m.role === "ai" ? (
                                // The summarizer emits markdown (lists, bold,
                                // tables); human input is untrusted free text
                                // and stays plain. react-markdown does not use
                                // dangerouslySetInnerHTML, so no XSS surface.
                                <Pane className="fms-markdown">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                                </Pane>
                            ) : (
                                <Paragraph whiteSpace="pre-wrap" wordBreak="break-word">{m.content}</Paragraph>
                            )}
                        </Pane>
                    ))}
                    {statusLabel !== null && (
                        <Pane display="flex" alignItems="center" gap={8} marginBottom={12}>
                            <Spinner size={16} />
                            <Text color="muted">{statusLabel}</Text>
                        </Pane>
                    )}
                    {error !== null && (
                        <Paragraph color="danger" marginBottom={12}>{error}</Paragraph>
                    )}
                    <div ref={bottomRef} />
                </Pane>
                <Pane display="flex" gap={8} marginTop={16}>
                    <TextInput
                        flex={1}
                        placeholder="e.g. What was total profit last month?"
                        value={draft}
                        disabled={isBusy}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onDraftChange(e.target.value)}
                        onKeyDown={(e: KeyboardEvent) => {
                            if (e.key === "Enter") {
                                onSubmit();
                            }
                        }}
                    />
                    <Button appearance="primary" disabled={isBusy} onClick={onSubmit}>
                        Send
                    </Button>
                </Pane>
            </Pane>

            <Pane width={260} flexShrink={0} borderLeft="muted" position="relative" display="flex" flexDirection="column">
                {/* Extra bottom padding keeps the last thread scrollable above
                    the floating new-conversation button. */}
                <Pane flex={1} overflowY="auto" padding={16} paddingBottom={80}>
                    {threadRows.map(row => (
                        <Pane
                            key={row.id}
                            className="fms-thread-row"
                            display="flex"
                            alignItems="center"
                            gap={4}
                            padding={8}
                            marginBottom={8}
                            borderRadius={4}
                            background={row.id === activeThreadId ? "tint2" : undefined}
                            cursor="pointer"
                            onClick={() => onOpenThread(row.id)}
                        >
                            <Pane flex={1} minWidth={0}>
                                <Text size={300} display="block" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                    {row.title}
                                </Text>
                                <Text size={300} color="muted">{row.timestamp}</Text>
                            </Pane>
                            {/* Revealed by .fms-thread-row:hover (and
                                focus-within for keyboard users), see FmsChat.css. */}
                            <IconButton
                                icon={TrashIcon}
                                size="small"
                                appearance="minimal"
                                intent="danger"
                                className="fms-thread-delete"
                                aria-label={`Delete conversation ${row.title}`}
                                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                    // Don't also open the thread underneath.
                                    e.stopPropagation();
                                    onRequestDelete(row.id);
                                }}
                            />
                        </Pane>
                    ))}
                </Pane>
                <IconButton
                    icon={PlusIcon}
                    appearance="primary"
                    aria-label="New conversation"
                    title="New conversation"
                    position="absolute"
                    bottom={16}
                    right={16}
                    width={48}
                    height={48}
                    borderRadius={24}
                    boxShadow="0 2px 8px rgba(16, 24, 64, 0.3)"
                    onClick={onStartNew}
                />
            </Pane>

            <Dialog
                isShown={pendingDeleteTitle !== null}
                title="Delete conversation"
                intent="danger"
                confirmLabel="Delete"
                onConfirm={(close) => {
                    onConfirmDelete();
                    close();
                }}
                onCloseComplete={onCancelDelete}
            >
                {`Delete "${pendingDeleteTitle ?? "Untitled"}"? This cannot be undone.`}
            </Dialog>
        </Pane>
    );
};

// Group gate via the standard wrapper, like every other gated page. It
// denies by default until the user resolves (no authorized-view flash on
// load), and the chat UI + its token-minting effects only mount once the
// user is in the group. Server-side /langgraph/token 403 stays the real
// access control; the inner component handles that via `blocked`.
export const FmsChatPage = () => (
    <RampartAuthorized
        requiredGroups={[FMS_CHAT_GROUP]}
        unauthorizedView={<InviteOnly />}
    >
        <FmsChat />
    </RampartAuthorized>
);
